import React, { useEffect, useMemo, useState } from "react";
import { Send } from "lucide-react";
import { productsService } from "./../../../../services/products.service";
import { ActionButton, SelectLike } from "./inventory-ui";
import { normalizeList } from "../utils/inventory.utils";

export default function AssignProductsModal({
  open,
  onClose,
  warehouses,
  onSubmit,
  saving,
}) {
  const [warehouseId, setWarehouseId] = useState("");
  const [note, setNote] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [qtyErrors, setQtyErrors] = useState({});
  const [purchasePriceErrors, setPurchasePriceErrors] = useState({});
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWarehouseId("");
    setNote("");
    setProductSearch("");
    setSelectedItems([]);
    setQtyErrors({});
    setPurchasePriceErrors({});
    setAvailableProducts([]);
  }, [open]);

  useEffect(() => {
    setSelectedItems([]);
    setQtyErrors({});
    setPurchasePriceErrors({});
  }, [warehouseId]);

  const loadAssignedProducts = async (targetWarehouseId, searchText = "") => {
    if (!targetWarehouseId) {
      setAvailableProducts([]);
      return;
    }

    try {
      setProductsLoading(true);

      const res = await productsService.listWarehouseAssignmentProducts({
        warehouseId: targetWarehouseId,
        q: searchText,
        page: 1,
        limit: 200,
      });

      const rawItems = normalizeList(res, ["items", "rows", "data"]);

      const flattened = rawItems.flatMap((product) => {
        const variants = Array.isArray(product?.variants) ? product.variants : [];

        return variants
          .filter((variant) => variant?.id && variant?.assigned !== false)
          .map((variant) => ({
            id: `${product.id}::${variant.id}`,
            rowId: `${product.id}::${variant.id}`,
            productId: product.id,
            productVariantId: variant.id,
            name: product.name || "Product",
            variantName:
              variant.label ||
              variant.variantName ||
              variant.variant_name ||
              variant.name ||
              variant.packLabel ||
              "Default",
            sku: variant.variantSku || variant.sku || product.sku || "",
            barcode: variant.barcode || product.barcode || "",
            categoryName: product.categoryName || "",
            brandName: product.brandName || "",
            uom: product.unitName || product.unitSymbol || "unit",
            mrp: variant.mrp ?? product.mrp ?? "",
            sellingPrice: variant.sellingPrice ?? product.sellingPrice ?? "",
            currency: variant.currency || product.currency || "INR",
            imageUrl: product.imageUrl || "",
          }));
      });

      setAvailableProducts(flattened);
    } catch (e) {
      setAvailableProducts([]);
      console.error("Failed to load assigned products", e);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const t = setTimeout(() => {
      if (warehouseId) {
        loadAssignedProducts(warehouseId, productSearch);
      } else {
        setAvailableProducts([]);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [open, warehouseId, productSearch]);

  const invalidSelectedItems = useMemo(() => {
    return selectedItems.filter(
      (item) => !String(item.qty ?? "").trim() || Number(item.qty) <= 0
    );
  }, [selectedItems]);

  const invalidPurchaseItems = useMemo(() => {
    return selectedItems.filter((item) => {
      const raw = String(item.purchasePrice ?? "").trim();
      const value = raw === "" ? NaN : Number(raw);
      return !(raw !== "" && Number.isFinite(value) && value >= 0);
    });
  }, [selectedItems]);

  const allSelectedItemsValid =
    selectedItems.length > 0 &&
    invalidSelectedItems.length === 0 &&
    invalidPurchaseItems.length === 0;

  const normalizedProducts = useMemo(() => {
    return Array.isArray(availableProducts) ? availableProducts : [];
  }, [availableProducts]);

  const filteredProducts = useMemo(() => {
    const q = String(productSearch || "").trim().toLowerCase();
    if (!q) return normalizedProducts;

    return normalizedProducts.filter((p) =>
      [p.name, p.variantName, p.sku, p.barcode, p.categoryName, p.brandName].some(
        (val) => String(val || "").toLowerCase().includes(q)
      )
    );
  }, [normalizedProducts, productSearch]);

  const isSelected = (rowId) =>
    selectedItems.some((item) => String(item.rowId) === String(rowId));

  const toggleProduct = (product) => {
    const rowId = String(product.rowId || product.id);

    setSelectedItems((prev) => {
      const exists = prev.some((item) => String(item.rowId) === rowId);

      if (exists) {
        return prev.filter((item) => String(item.rowId) !== rowId);
      }

      return [
        ...prev,
        {
          rowId,
          productId: product.productId,
          productVariantId: product.productVariantId,
          productName: product.name,
          variantName: product.variantName || "Default",
          sku: product.sku,
          qty: "",
          uom: product.uom || "unit",
          note: "",
          purchasePrice: "",
          purchaseCurrency: product.currency || "INR",
          mrp: product.mrp ?? "",
          sellingPrice: product.sellingPrice ?? "",
        },
      ];
    });

    setQtyErrors((prev) => {
      const next = { ...prev };
      if (next[rowId]) delete next[rowId];
      return next;
    });

    setPurchasePriceErrors((prev) => {
      const next = { ...prev };
      if (next[rowId]) delete next[rowId];
      return next;
    });
  };

  const updateSelectedItem = (rowId, patch) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        String(item.rowId) === String(rowId) ? { ...item, ...patch } : item
      )
    );

    if (Object.prototype.hasOwnProperty.call(patch, "qty")) {
      const qtyValue = String(patch.qty ?? "").trim();
      setQtyErrors((prev) => {
        const next = { ...prev };
        if (qtyValue && Number(qtyValue) > 0) {
          delete next[rowId];
        } else {
          next[rowId] = "Please fill qty";
        }
        return next;
      });
    }

    if (Object.prototype.hasOwnProperty.call(patch, "purchasePrice")) {
      const raw = String(patch.purchasePrice ?? "").trim();
      const value = raw === "" ? NaN : Number(raw);

      setPurchasePriceErrors((prev) => {
        const next = { ...prev };
        if (raw !== "" && Number.isFinite(value) && value >= 0) {
          delete next[rowId];
        } else {
          next[rowId] = "Please fill valid purchase price";
        }
        return next;
      });
    }
  };

  const removeSelectedItem = (rowId) => {
    setSelectedItems((prev) =>
      prev.filter((item) => String(item.rowId) !== String(rowId))
    );

    setQtyErrors((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });

    setPurchasePriceErrors((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const submit = () => {
    if (!warehouseId) {
      alert("Please select warehouse");
      return;
    }

    if (!selectedItems.length) {
      alert("Please select at least one variant");
      return;
    }

    const invalidItems = selectedItems.filter(
      (item) => !String(item.qty ?? "").trim() || Number(item.qty) <= 0
    );

    if (invalidItems.length > 0) {
      const nextErrors = {};
      invalidItems.forEach((item) => {
        nextErrors[item.rowId] = "Please fill qty";
      });
      setQtyErrors(nextErrors);
      alert("Please fill qty for all selected variants");
      return;
    }

    const invalidPurchaseRows = selectedItems.filter((item) => {
      const raw = String(item.purchasePrice ?? "").trim();
      const value = raw === "" ? NaN : Number(raw);
      return !(raw !== "" && Number.isFinite(value) && value >= 0);
    });

    if (invalidPurchaseRows.length > 0) {
      const nextErrors = {};
      invalidPurchaseRows.forEach((item) => {
        nextErrors[item.rowId] = "Please fill valid purchase price";
      });
      setPurchasePriceErrors(nextErrors);
      alert("Please fill purchase price for all selected variants");
      return;
    }

    const selectedWarehouse = warehouses.find(
      (w) => String(w?.id) === String(warehouseId)
    );

    const payload = {
      warehouseId,
      warehouseName: selectedWarehouse?.name || selectedWarehouse?.code || "",
      note: note.trim(),
      items: selectedItems.map((item) => ({
        productId: item.productId,
        productVariantId: item.productVariantId,
        productName: item.productName || item.name || "",
        variantName: item.variantName || "",
        sku: item.sku || "",
        qty: Number(item.qty),
        uom: item.uom || "unit",
        note: item.note?.trim() || undefined,
        purchasePrice: Number(item.purchasePrice),
        purchaseCurrency: item.purchaseCurrency || "INR",
      })),
    };

    onSubmit(payload);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/45 p-4">
      <div className="flex h-[94vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f4d8dc] px-6 py-5">
          <div>
            <div className="text-[24px] font-bold text-slate-800">
              Transfer Stock to Warehouse
            </div>
            <div className="mt-1 text-[13px] text-slate-500">
              Select multiple product variants and assign quantities to the warehouse.
              This will create a pending inbound receipt.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 text-[13px] font-semibold text-[#d86a78] hover:bg-[#fff7f8]"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 pb-24">
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.1fr_1fr]">
              <SelectLike
                value={warehouseId}
                onChange={setWarehouseId}
                placeholder="Select warehouse"
                searchable
                searchPlaceholder="Search warehouse"
                options={warehouses.map((w) => ({
                  value: w.id,
                  label: w.name || w.code || w.id,
                }))}
              />

              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional transfer note"
                className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
              />
            </div>

            <div className="grid min-h-0 grid-cols-1 gap-5 xl:grid-cols-[1fr_1.15fr]">
              <div className="rounded-[18px] border border-[#f3d8dd]">
                <div className="border-b border-[#f4d8dc] px-4 py-4">
                  <div className="text-[16px] font-bold text-slate-800">
                    Select Variants
                  </div>
                  <div className="mt-1 text-[12px] text-slate-500">
                    Search and select multiple assigned variants
                  </div>

                  <div className="mt-3">
                    <input
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search by product name, variant, SKU, barcode, category, brand"
                      className="h-[42px] w-full rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                    />
                  </div>
                </div>

                <div className="max-h-[460px] overflow-auto p-3">
                  {!warehouseId ? (
                    <div className="rounded-[14px] border border-dashed border-[#f2c6cc] px-4 py-8 text-center text-[13px] text-slate-500">
                      Please select warehouse first.
                    </div>
                  ) : productsLoading ? (
                    <div className="rounded-[14px] border border-dashed border-[#f2c6cc] px-4 py-8 text-center text-[13px] text-slate-500">
                      Loading assigned variants...
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="rounded-[14px] border border-dashed border-[#f2c6cc] px-4 py-8 text-center text-[13px] text-slate-500">
                      No assigned variants found for this warehouse.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredProducts.map((product) => {
                        const active = isSelected(product.rowId);

                        return (
                          <button
                            key={product.rowId}
                            type="button"
                            onClick={() => toggleProduct(product)}
                            className={
                              "flex w-full items-start gap-3 rounded-[16px] border p-4 text-left transition " +
                              (active
                                ? "border-[#d86a78] bg-[#fff3f5]"
                                : "border-[#f3d8dd] bg-white hover:bg-[#fffafb]")
                            }
                          >
                            <div className="pt-0.5">
                              <div
                                className={
                                  "flex h-5 w-5 items-center justify-center rounded border " +
                                  (active
                                    ? "border-[#d86a78] bg-[#d86a78] text-white"
                                    : "border-[#d8c7cb] bg-white text-transparent")
                                }
                              >
                                ✓
                              </div>
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="text-[14px] font-bold text-slate-800">
                                  {product.name}
                                </div>

                                {product.sku && (
                                  <span className="rounded-full bg-white px-2 py-1 text-[11px] text-slate-600">
                                    SKU: {product.sku}
                                  </span>
                                )}
                              </div>

                              <div className="mt-1 text-[12px] font-medium text-[#d86a78]">
                                Variant: {product.variantName || "Default"}
                              </div>

                              <div className="mt-2 text-[12px] text-slate-500">
                                {product.categoryName && <span>{product.categoryName}</span>}
                                {product.brandName && <span> • {product.brandName}</span>}
                                {product.uom && <span> • UOM: {product.uom}</span>}
                                {product.sellingPrice !== "" &&
                                  product.sellingPrice !== null &&
                                  product.sellingPrice !== undefined && (
                                    <span> • SP: ₹{product.sellingPrice}</span>
                                  )}

                                {product.mrp !== "" &&
                                  product.mrp !== null &&
                                  product.mrp !== undefined && (
                                    <span> • MRP: ₹{product.mrp}</span>
                                  )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-[18px] border border-[#f3d8dd]">
                <div className="border-b border-[#f4d8dc] px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[16px] font-bold text-slate-800">
                        Selected Variants
                      </div>

                      <div className="mt-3 rounded-[12px] border border-[#f2c6cc] bg-[#fff8f9] px-3 py-2 text-[12px] text-slate-600">
                        Note: If a new product needs to be assigned to a warehouse, the admin must first assign that variant to the warehouse.
                      </div>
                      <div className="mt-1 text-[12px] text-slate-500">
                        Enter quantity, purchase price and item note for selected variants
                      </div>
                    </div>

                    <div className="rounded-full bg-[#fff3f5] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
                      {selectedItems.length} selected
                    </div>
                  </div>
                </div>

                <div className="max-h-[460px] overflow-auto p-3">
                  {selectedItems.length === 0 ? (
                    <div className="rounded-[14px] border border-dashed border-[#f2c6cc] px-4 py-8 text-center text-[13px] text-slate-500">
                      No variant selected yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedItems.map((item) => (
                        <div
                          key={item.rowId}
                          className="rounded-[16px] border border-[#f6d6db] bg-[#fffafb] p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-[14px] font-bold text-slate-800">
                                {item.productName}
                              </div>
                              <div className="mt-1 text-[12px] font-medium text-[#d86a78]">
                                Variant: {item.variantName || "Default"}
                              </div>
                              <div className="mt-1 text-[12px] text-slate-500">
                                {item.sku ? `SKU: ${item.sku}` : "No SKU"}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => removeSelectedItem(item.rowId)}
                              className="rounded-[10px] border border-[#f2c6cc] px-3 py-2 text-[12px] font-semibold text-rose-600 hover:bg-rose-50"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                              <input
                                value={item.qty}
                                onChange={(e) =>
                                  updateSelectedItem(item.rowId, {
                                    qty: e.target.value,
                                  })
                                }
                                placeholder="Qty"
                                type="number"
                                min="0"
                                className={
                                  "h-[42px] w-full rounded-[12px] border px-4 text-[13px] outline-none focus:ring-2 " +
                                  (qtyErrors[item.rowId]
                                    ? "border-rose-400 focus:border-rose-500 focus:ring-rose-100"
                                    : "border-[#f2c6cc] focus:border-[#d86a78] focus:ring-[#f9dfe4]")
                                }
                              />
                              {qtyErrors[item.rowId] ? (
                                <div className="mt-1 text-[11px] font-medium text-rose-600">
                                  {qtyErrors[item.rowId]}
                                </div>
                              ) : null}
                            </div>

                            <input
                              value={item.uom}
                              onChange={(e) =>
                                updateSelectedItem(item.rowId, {
                                  uom: e.target.value,
                                })
                              }
                              placeholder="UOM"
                              className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                            />

                            <div>
                              <input
                                value={item.purchasePrice}
                                onChange={(e) =>
                                  updateSelectedItem(item.rowId, {
                                    purchasePrice: e.target.value,
                                  })
                                }
                                placeholder="Purchase Price"
                                type="number"
                                min="0"
                                step="0.01"
                                className={
                                  "h-[42px] w-full rounded-[12px] border px-4 text-[13px] outline-none focus:ring-2 " +
                                  (purchasePriceErrors[item.rowId]
                                    ? "border-rose-400 focus:border-rose-500 focus:ring-rose-100"
                                    : "border-[#f2c6cc] focus:border-[#d86a78] focus:ring-[#f9dfe4]")
                                }
                              />
                              {purchasePriceErrors[item.rowId] ? (
                                <div className="mt-1 text-[11px] font-medium text-rose-600">
                                  {purchasePriceErrors[item.rowId]}
                                </div>
                              ) : null}
                            </div>

                            <input
                              value={item.purchaseCurrency || "INR"}
                              onChange={(e) =>
                                updateSelectedItem(item.rowId, {
                                  purchaseCurrency: e.target.value.toUpperCase(),
                                })
                              }
                              placeholder="Currency"
                              className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] uppercase outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                            />

                            <input
                              value={item.note}
                              onChange={(e) =>
                                updateSelectedItem(item.rowId, {
                                  note: e.target.value,
                                })
                              }
                              placeholder="Item note"
                              className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-[#f4d8dc] bg-white px-6 py-4 shadow-[0_-8px_20px_rgba(15,23,42,0.04)]">
          <div className="text-[13px] text-slate-500"></div>

          <ActionButton
            onClick={submit}
            disabled={saving || !warehouseId || !allSelectedItemsValid}
            className="min-w-[160px] shrink-0"
          >
            <Send size={16} />
            {saving ? "Assigning..." : "Transfer Stock"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}